<template>
  <div>
    <Steps :active="3" />
    <form @submit.prevent="send">
      <div class="flex flex-col justify-center content-center">
        <div class="font-bold text-2xl text-gray-700 mx-auto mb-4">Comments and Photos</div>

        <div v-for="(product, index) in returns" :key="index">
          <div class="flex flex-col mb-4 rounded bg-white max-w-screen-md mx-auto shadow-lg">
            <div class="bg-gray-700 w-100 p-4 text-white rounded-t">Return {{(index + 1)}} of {{returns.length}}</div>
            <div class="p-4">{{product.name}}</div>

            <div class="bg-gray-700 w-100 p-4 text-white">Why are you returning this?</div>
            <div class="p-4">
              <div
                class="flex flex-col text-sm text-gray-700"
                :class="{'mb-3': (idxReason + 1) < reasonList.length}"
                v-for="(reason, idxReason) in reasonList"
                :key="reason.title"
              >
                <label>
                  <input
                    type="radio"
                    :name="'reason' + index"
                    :value="reason.title"
                    @change="setReturnReason(product, reason)"
                  />
                  {{reason.title}}
                </label>
              </div>
            </div>

            <div v-if="product.reason">
              <div
                v-if="product.reason.condition.requiresInspection && orderDetails.daysSincePurchase > 30 && orderDetails.daysSincePurchase <= 366"
              >
                <div class="bg-red-700 w-100 p-4 text-white">Inspection Required</div>
                <div class="p-4 text-sm">
                  <div class="mb-4">
                    Subject to inspection confirmation of being faulty the product will be sent to our technicians for repair.
                    If a repair is not possible, we will offer a refund including postage costs. The 12 month warranty starts
                    from the date of the original purchase.
                  </div>

                  <div>
                    <strong>Please note:</strong>
                    If a product is returned as faulty but not found to have a manufacturing fault or if a fault has developed
                    as a result of misuse, no product or postage costs will be refunded.
                  </div>
                </div>
              </div>

              <div v-if="product.reason.condition.mustBeUnused">
                <div class="bg-red-700 w-100 p-4 text-white">Your item must be unused</div>
                <div class="p-4 text-sm">
                  <div class="mb-4">
                    Subject to inspection confirmation your item will be checked if it was used and if it's in its
                    original packaging.
                  </div>
                  <div>
                    <strong>Please note:</strong>
                    If a product is returned without its original packaging or used, no product or postage costs will be refunded.
                  </div>
                </div>
              </div>

              <div class="bg-gray-700 w-100 p-4 text-white">
                Comments
                <span v-if="product.reason.comment.required">(required)</span>
                <span v-else>(optional)</span>
              </div>
              <div class="flex flex-col p-4">
                <div
                  class="mb-4 text-sm"
                  :class="{'text-red-600': product.reason.comment.required}"
                  v-if="product.reason.comment.description"
                >{{product.reason.comment.description}}</div>
                <textarea
                  class="border rounded w-full h-48 text-sm p-2"
                  :required="product.reason.comment.required"
                  v-model="product.reason.comment.details"
                ></textarea>
                <div class="mt-4 text-sm text-gray-600">
                  Do not include personal information as these comments may be shared with external service providers to
                  identify product defects.
                </div>
              </div>

              <div class="bg-gray-700 w-100 p-4 text-white">
                Photos
                <span v-if="product.reason.photo.required">(required)</span>
                <span v-else>(optional)</span>
              </div>

              <div class="flex flex-col p-4 pb-0">
                <div
                  class="text-sm mb-4 text-red-600"
                  v-if="product.reason.photo.required"
                >To help deal with your return, you must upload one or more photos.</div>
                <div class="text-sm mb-4" v-else>To help deal with your return, you can upload one or more photos.</div>

                <Upload :list.sync="product.reason.photo.list" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-center" v-if="isStepValid">
        <ButtonNext class="bg-gray-700" />
      </div>
      <div
        class="flex justify-center m-auto text-sm bg-orange-500 text-white p-4 rounded max-w-md"
        v-else
      >Please complete all fields above for each return.</div>
    </form>
  </div>
</template>

<script>
import ButtonNext from '@/components/ButtonNext.vue'
import Steps from '@/components/Steps.vue'
import Upload from '@/components/Upload.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {
    ButtonNext,
    Steps,
    Upload,
  },
  data: function() {
    return {
      photos: [],
      reasonListAll: [
        {
          title: 'Accidental order',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: true,
            requiresInspection: false,
          },
          comment: {
            required: false,
            details: '',
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'Better price available',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: true,
            requiresInspection: false,
          },
          comment: {
            required: true,
            details: '',
            description: 'Please enter the name of the store and the lower price offered.',
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'Performance or quality not adequate',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: false,
            requiresInspection: true,
          },
          comment: {
            required: true,
            details: '',
            description: "Describe what's wrong with the item.",
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'Incompatible or not useful for intended purpose',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: false,
            requiresInspection: false,
          },
          comment: {
            required: true,
            details: '',
            description: "Describe what's wrong with the item.",
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'The item is damaged but the box or envelope is undamaged',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: false,
            requiresInspection: true,
          },
          comment: {
            required: false,
            details: '',
          },
          photo: {
            required: true,
            list: [],
          },
        },
        {
          title: 'Missed estimated delivery date',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: true,
            requiresInspection: false,
          },
          comment: {
            required: false,
            details: '',
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'Missing parts or accessories',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: false,
            requiresInspection: true,
          },
          comment: {
            required: true,
            details: '',
            description: "Describe what's wrong with the item.",
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'The item and the box or envelope it came in are both damaged',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: false,
            requiresInspection: true,
          },
          comment: {
            required: true,
            details: '',
            description: "Describe what's wrong with the item.",
          },
          photo: {
            required: true,
            list: [],
          },
        },
        {
          title: 'Wrong item was sent',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: true,
            requiresInspection: false,
          },
          comment: {
            required: true,
            details: '',
            description: 'Please enter the name of the item received in error.',
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: "Item defective or doesn't work",
          condition: {
            daysSincePurchaseLimit: 366,
            mustBeUnused: false,
            requiresInspection: true,
          },
          comment: {
            required: true,
            details: '',
            description: "Describe what's wrong with the item.",
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'Arrived in addition to what was ordered',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: true,
            requiresInspection: false,
          },
          comment: {
            required: true,
            details: '',
            description: 'Please enter the name of the item received in error.',
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'No longer needed',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: true,
            requiresInspection: false,
          },
          comment: {
            required: false,
            details: '',
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'Unauthorised purchase',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: true,
            requiresInspection: false,
          },
          comment: {
            required: false,
            details: '',
          },
          photo: {
            required: false,
            list: [],
          },
        },
        {
          title: 'Description on website was not accurate',
          condition: {
            daysSincePurchaseLimit: 30,
            mustBeUnused: false,
            requiresInspection: false,
          },
          comment: {
            required: true,
            details: '',
            description: "Explain what's wrong with the website description.",
          },
          photo: {
            required: false,
            list: [],
          },
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['returns', 'orderDetails']),
    reasonList() {
      return this.reasonListAll.filter(
        i => i.condition.daysSincePurchaseLimit >= this.orderDetails.daysSincePurchase
      )
    },
    isStepValid() {
      return (
        this.returns.filter(i => {
          if (!i.reason) {
            return true
          } else if (i.reason.comment.required && i.reason.comment.details.length <= 3) {
            return true
          } else if (i.reason.photo.required && i.reason.photo.list.length == 0) {
            return true
          }

          return false
        }).length == 0
      )
    },
  },
  mounted() {
    // this.$refs.pond.getFiles().filter(x => x.status !== 5).length !== 0
  },
  methods: {
    ...mapActions(['setReturns']),
    send() {
      this.setReturns(this.returns).then(() => this.$router.push('step4'))
    },
    setReturnReason(product, reason) {
      try {
        reason.comment.details = product.reason.comment.details ?? ''
        reason.photo.list = product.reason.photo.list ?? []
      } catch (e) {
        // ignored
      }

      this.$set(product, 'reason', JSON.parse(JSON.stringify(reason)))
    },
  },
}
</script>
